import { useEffect, useRef, useState } from "react";
import {
  FaGlobe,
  FaMagic,
  FaInfoCircle,
  FaCopy
} from "react-icons/fa";
import { getCachedStoreSettings } from "../../common/SEOHead";
import { connectSeoText } from "../../../util/storeLogoHelper";

/**
 * Clean & truncate description to optimal SEO length (155-160 chars)
 */
const cleanSnippetText = (text, maxLength = 158) => {
  if (!text) return "";
  const clean = text
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  if (clean.length <= maxLength) return clean;
  const truncated = clean.substring(0, maxLength);
  const lastSpace = truncated.lastIndexOf(" ");
  return (lastSpace > 110 ? truncated.substring(0, lastSpace) : truncated) + "...";
};

export default function ShopifySEOEditor({
  itemName = "",
  itemBrand = "",
  itemCategoryName = "",
  itemDescription = "",
  itemSlug = "",
  metaTitle = "",
  metaDescription = "",
  metaKeywords = "",
  storeName = "",
  titleSeparator = "–",
  baseUrl = "",
  type = "product", // "product" or "category"
  onChange,
}) {
  const [copied, setCopied] = useState(false);
  const [seoSettings, setSeoSettings] = useState({});

  useEffect(() => {
    let live = true;
    getCachedStoreSettings().then((settings) => {
      if (live) setSeoSettings(settings || {});
    });
    return () => {
      live = false;
    };
  }, []);

  const liveName = (seoSettings.storeName || storeName || "Store").trim() || "Store";
  const liveSeparator = (seoSettings.titleSeparator || titleSeparator || "–").trim() || "–";
  const liveBase = (seoSettings.canonicalBaseUrl || baseUrl || (typeof window !== "undefined" ? window.location.origin : "")).replace(/\/+$/, "");
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => {
    if (!onChangeRef.current || !liveName || liveName.toLowerCase().includes("gootocart")) return;
    const next = {};
    const title = connectSeoText(metaTitle, liveName);
    const description = connectSeoText(metaDescription, liveName);
    const keywords = connectSeoText(metaKeywords, liveName);
    if (metaTitle && title !== metaTitle) next.meta_title = title;
    if (metaDescription && description !== metaDescription) next.meta_descrip = description;
    if (metaKeywords && keywords !== metaKeywords) next.meta_keyword = keywords;
    if (Object.keys(next).length) onChangeRef.current(next);
  }, [liveName, metaTitle, metaDescription, metaKeywords]);

  // Auto-calculated Fallbacks (Exact Shopify behavior when left empty)
  const autoTitle =
    type === "product"
      ? `${itemName || "Product Name"}${itemBrand ? ` ${liveSeparator} ${itemBrand}` : ""} ${liveSeparator} ${liveName}`
      : `${itemName || "Collection Name"} Collection ${liveSeparator} ${liveName}`;

  const autoDescription =
    cleanSnippetText(itemDescription) ||
    (type === "product"
      ? `Buy ${itemName || "this item"}${itemBrand ? ` by ${itemBrand}` : ""} online at ${liveName}. Nationwide fast delivery, genuine quality, and Cash on Delivery.`
      : `Explore the complete ${itemName || "featured"} collection at ${liveName}. Best prices with nationwide delivery and Cash on Delivery.`);

  const previewTitle = metaTitle && metaTitle.trim() ? metaTitle.trim() : autoTitle;
  const previewDescription =
    metaDescription && metaDescription.trim()
      ? cleanSnippetText(metaDescription)
      : autoDescription;

  const urlPath =
    type === "product"
      ? `/collection/${itemCategoryName ? itemCategoryName.toLowerCase().replace(/[\s\W-]+/g, "-") : "all"}/${itemSlug || "product-handle"}`
      : `/collection/${itemSlug || "collection-handle"}`;

  const previewFullUrl = `${liveBase}${urlPath}`;

  // Character lengths
  const titleLength = (metaTitle || "").length;
  const descLength = (metaDescription || "").length;

  const getTitleStatus = () => {
    if (!metaTitle) return { label: "Auto Fallback Active", color: "text-muted", badge: "bg-light text-dark" };
    if (titleLength >= 40 && titleLength <= 60) {
      return { label: "Optimal length", color: "text-success", badge: "bg-success" };
    }
    if (titleLength > 60) {
      return { label: "May get truncated in search results", color: "text-warning", badge: "bg-warning text-dark" };
    }
    return { label: "A bit short for search ranking", color: "text-muted", badge: "bg-secondary" };
  };

  const getDescStatus = () => {
    if (!metaDescription) return { label: "Auto Fallback Active", color: "text-muted", badge: "bg-light text-dark" };
    if (descLength >= 120 && descLength <= 160) {
      return { label: "Optimal length", color: "text-success", badge: "bg-success" };
    }
    if (descLength > 160) {
      return { label: "Over recommended 160 chars", color: "text-warning", badge: "bg-warning text-dark" };
    }
    return { label: "Good summary", color: "text-primary", badge: "bg-info text-dark" };
  };

  // Shopify Auto-Generate One-Click Magic
  const handleAutoGenerate = () => {
    const generatedTitle =
      type === "product"
        ? `${itemName}${itemBrand ? ` ${liveSeparator} ${itemBrand}` : ""} ${liveSeparator} ${liveName}`
        : `${itemName} Collection ${liveSeparator} ${liveName}`;

    let generatedDesc = cleanSnippetText(itemDescription, 155);
    if (!generatedDesc) {
      generatedDesc =
        type === "product"
          ? `Buy ${itemName}${itemBrand ? ` by ${itemBrand}` : ""} online at ${liveName}. Best price in Pakistan, fast shipping, and Cash on Delivery (COD).`
          : `Shop authentic ${itemName} products online at ${liveName}. Enjoy fast nationwide delivery and 100% genuine guaranteed items.`;
    }

    const keywordList = [];
    if (itemName) keywordList.push(itemName.toLowerCase());
    if (itemBrand) keywordList.push(itemBrand.toLowerCase());
    if (itemCategoryName) keywordList.push(itemCategoryName.toLowerCase());
    if (liveName) keywordList.push(liveName.toLowerCase());
    keywordList.push("buy online", "cash on delivery", "best price", "pakistan");
    const generatedKeywords = Array.from(new Set(keywordList)).join(", ");

    if (onChange) {
      onChange({
        meta_title: generatedTitle,
        meta_descrip: generatedDesc,
        meta_keyword: generatedKeywords,
      });
    }
  };

  const handleCopyUrl = () => {
    navigator.clipboard.writeText(previewFullUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const titleStatus = getTitleStatus();
  const descStatus = getDescStatus();

  return (
    <div className="shopify-seo-editor admin-card bg-white rounded-3 shadow-sm border mb-4">
      <div className="admin-card-header bg-white py-3 px-4 border-bottom d-flex align-items-center justify-content-between flex-wrap gap-2">
        <div className="d-flex align-items-center gap-2">
          <FaGlobe className="text-primary" />
          <h5 className="admin-card-title mb-0 fw-bold" style={{ fontSize: "15px" }}>
            Search Engine Listing Preview
          </h5>
          <span className="badge bg-light text-muted border ms-2" style={{ fontSize: "11px" }}>
            On-page SEO
          </span>
        </div>
        <button
          type="button"
          onClick={handleAutoGenerate}
          className="btn btn-outline-primary btn-sm rounded-pill d-inline-flex align-items-center gap-2 fw-semibold px-3 shadow-sm"
          style={{ fontSize: "12px" }}
          title="Auto-generate optimized SEO title, description, and keywords based on item details"
        >
          <FaMagic className="text-warning" /> Auto-generate
        </button>
      </div>

      <div className="admin-card-body p-4">
        {/* Google SERP Live Search Result Snippet Card */}
        <div
          className="p-3 mb-4 rounded-3 border"
          style={{
            background: "#ffffff",
            boxShadow: "0 1px 6px rgba(32,33,36,0.12)",
            maxWidth: "620px",
          }}
        >
          <div className="d-flex align-items-center justify-content-between mb-1">
            <div className="d-flex align-items-center gap-2 text-muted" style={{ fontSize: "12px" }}>
              <div
                style={{
                  width: "18px",
                  height: "18px",
                  borderRadius: "50%",
                  background: "#e8f0fe",
                  color: "#1a73e8",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "10px",
                  fontWeight: "bold",
                }}
              >
                G
              </div>
              <span className="text-truncate font-monospace" style={{ color: "#202124" }}>
                {previewFullUrl}
              </span>
            </div>
            <button
              type="button"
              onClick={handleCopyUrl}
              className="btn btn-link btn-sm p-0 text-muted"
              title="Copy URL"
              style={{ fontSize: "11px" }}
            >
              {copied ? <span className="text-success fw-bold">Copied!</span> : <FaCopy />}
            </button>
          </div>

          <h6
            className="mb-1 text-truncate fw-semibold"
            style={{
              color: "#1a0dab",
              fontSize: "17px",
              lineHeight: "1.3",
              cursor: "pointer",
              fontFamily: "arial, sans-serif",
            }}
          >
            {previewTitle}
          </h6>

          <p
            className="mb-0 text-muted"
            style={{
              fontSize: "13.5px",
              lineHeight: "1.45",
              color: "#4d5156",
              fontFamily: "arial, sans-serif",
              wordBreak: "break-word",
            }}
          >
            {previewDescription}
          </p>
        </div>

        {/* Live Fallback Explanation Notice */}
        {!metaTitle && !metaDescription && (
          <div className="alert alert-light border rounded-3 p-2 mb-3 d-flex align-items-center gap-2" style={{ fontSize: "12px" }}>
            <FaInfoCircle className="text-info flex-shrink-0" />
            <span>
              <strong>Auto SEO is on:</strong> When left blank, your store automatically generates rich titles, descriptions, OpenGraph social cards, and Schema.org rich snippets from your item information.
            </span>
          </div>
        )}

        {/* SEO Input Fields */}
        <div className="row g-3">
          {/* Page Meta Title */}
          <div className="col-12">
            <div className="d-flex align-items-center justify-content-between mb-1">
              <label className="form-label fw-semibold text-dark small mb-0">
                Page Meta Title Tag
              </label>
              <div className="d-flex align-items-center gap-2">
                <span className={`badge ${titleStatus.badge}`} style={{ fontSize: "10.5px" }}>
                  {titleLength} / 60 characters
                </span>
                <small className={titleStatus.color} style={{ fontSize: "11px" }}>
                  {titleStatus.label}
                </small>
              </div>
            </div>
            <input
              type="text"
              name="meta_title"
              value={metaTitle || ""}
              onChange={(e) => onChange && onChange({ meta_title: e.target.value })}
              placeholder={autoTitle}
              className="form-control"
              style={{ fontSize: "13.5px" }}
            />
            {!metaTitle && (
              <small className="text-muted d-block mt-1 fst-italic" style={{ fontSize: "11px" }}>
                Auto-fallback: {autoTitle}
              </small>
            )}
          </div>

          {/* Meta Keywords */}
          <div className="col-12">
            <div className="d-flex align-items-center justify-content-between mb-1">
              <label className="form-label fw-semibold text-dark small mb-0">
                Meta Keywords
              </label>
              <small className="text-muted" style={{ fontSize: "11px" }}>
                Comma-separated search tags
              </small>
            </div>
            <input
              type="text"
              name="meta_keyword"
              value={metaKeywords || ""}
              onChange={(e) => onChange && onChange({ meta_keyword: e.target.value })}
              placeholder="e.g. premium apparel, buy online, summer fashion, cash on delivery"
              className="form-control"
              style={{ fontSize: "13.5px" }}
            />
          </div>

          {/* Meta Description */}
          <div className="col-12">
            <div className="d-flex align-items-center justify-content-between mb-1">
              <label className="form-label fw-semibold text-dark small mb-0">
                Page Meta Description
              </label>
              <div className="d-flex align-items-center gap-2">
                <span className={`badge ${descStatus.badge}`} style={{ fontSize: "10.5px" }}>
                  {descLength} / 160 characters
                </span>
                <small className={descStatus.color} style={{ fontSize: "11px" }}>
                  {descStatus.label}
                </small>
              </div>
            </div>
            <textarea
              name="meta_descrip"
              rows="3"
              value={metaDescription || ""}
              onChange={(e) => onChange && onChange({ meta_descrip: e.target.value })}
              placeholder={autoDescription}
              className="form-control"
              style={{ fontSize: "13.5px" }}
            ></textarea>
            {!metaDescription && (
              <small className="text-muted d-block mt-1 fst-italic" style={{ fontSize: "11px" }}>
                Auto-fallback: {autoDescription}
              </small>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
